import React, { useMemo, useState, Fragment, createElement } from 'react';
import { Crown, Check, CheckCircle2, ShieldCheck, LayoutGrid, Target, Megaphone, Zap, TrendingUp, ShoppingBag, X, ArrowRight, Clock, Eye, MousePointerClick, DollarSign, ChevronDown, ChevronUp, RotateCcw, CalendarPlus, Rocket, ArrowUpCircle, Mail, Sparkles, BarChart3, Wallet, AlertCircle, Globe, BookOpen, Pin, FileText, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { ALL_AD_SLOTS } from '../ads/adData';
interface PromotionTabProps {
  showToast: (msg: string) => void;
  setActiveAutoService: (service: string | null) => void;
  setActiveTab?: (tab: string) => void;
}
interface AdOrder {
  id: string;
  type: 'banner' | 'email' | 'pin';
  name: string;
  status: 'active' | 'completed' | 'pending_moderation' | 'pending_payment' | 'approved' | 'rejected' | 'pending';
  startDate: string;
  endDate: string | null;
  price: number;
  views: number;
  clicks: number;
  orders: number;
  income: number;
  rejectionReason?: string;
  moderatedAt?: string;
  // Creative materials
  adTitle?: string;
  adDescription?: string;
  adLink?: string;
  adImageUrl?: string;
}
// Map ALL_AD_SLOTS to astrologer-friendly format
const SLOT_ICON_MAP: Record<string, {
  icon: any;
  iconCls: string;
  activeCls: string;
}> = {
  Banner: {
    icon: LayoutGrid,
    iconCls: 'bg-indigo-100 text-indigo-600',
    activeCls: 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500'
  },
  Native: {
    icon: Layers,
    iconCls: 'bg-amber-100 text-amber-600',
    activeCls: 'border-amber-500 bg-amber-50/50 ring-1 ring-amber-500'
  },
  Pinned: {
    icon: Pin,
    iconCls: 'bg-emerald-100 text-emerald-600',
    activeCls: 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500'
  },
  Email: {
    icon: Mail,
    iconCls: 'bg-purple-100 text-purple-600',
    activeCls: 'border-purple-500 bg-purple-50/50 ring-1 ring-purple-500'
  },
  Sticky: {
    icon: Target,
    iconCls: 'bg-rose-100 text-rose-600',
    activeCls: 'border-rose-500 bg-rose-50/50 ring-1 ring-rose-500'
  }
};
export function PromotionTab({
  showToast,
  setActiveAutoService,
  setActiveTab
}: PromotionTabProps) {
  // Dynamic ad slots from shared adData (synced with admin)
  const availableAdSlots = useMemo(() => {
    return ALL_AD_SLOTS.filter(s => s.status === 'active').map(slot => {
      const style = SLOT_ICON_MAP[slot.type] || SLOT_ICON_MAP.Banner;
      const weeklyPrice = Math.round(slot.basePrice / 4);
      const estimatedReach = Math.round(slot.basePrice / 1.5);
      return {
        id: slot.id,
        name: slot.name,
        where: `${slot.page} · ${slot.type} · ${slot.size}`,
        price: `${weeklyPrice.toLocaleString('ru-RU')} ₽ / неделя`,
        priceNum: weeklyPrice,
        reach: `~${estimatedReach.toLocaleString('ru-RU')} показов`,
        requirements: slot.type === 'Pinned' ? 'Профиль заполнен на 80%+' : slot.type === 'Email' ? 'Текст до 200 символов, ссылка на профиль' : `Изображение ${slot.size}, до 50 символов текста`,
        icon: style.icon,
        activeCls: style.activeCls,
        iconCls: style.iconCls,
        slotType: slot.type
      };
    });
  }, []);
  // State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<'pro' | 'top' | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedPinCategory, setSelectedPinCategory] = useState<string>('');
  const [selectedPinDuration, setSelectedPinDuration] = useState<'1week' | '2weeks' | '1month'>('1week');
  const [showNewAdModal, setShowNewAdModal] = useState(false);
  const [selectedAdSlot, setSelectedAdSlot] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [adOrdersPage, setAdOrdersPage] = useState(1);
  const AD_ORDERS_PER_PAGE = 5;
  // Ad Wizard State
  const [adWizardStep, setAdWizardStep] = useState<1 | 2 | 3>(1);
  const [adMaterials, setAdMaterials] = useState({
    title: '',
    description: '',
    link: '',
    imageUrl: ''
  });
  const [adBalance, setAdBalance] = useState(4500);
  // Mock Data
  const [adOrders, setAdOrders] = useState<AdOrder[]>([{
    id: 'ORD-A-001',
    type: 'banner',
    name: 'Баннер на главной',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '07.03.2026',
    price: 5000,
    views: 12400,
    clicks: 620,
    orders: 18,
    income: 54000,
    adTitle: 'Профессиональный разбор натальной карты',
    adDescription: 'Узнайте свои сильные стороны, кармические задачи и жизненное предназначение. 15 лет опыта, 3000+ консультаций.',
    adLink: '/astrologer/elena-star',
    adImageUrl: 'https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f0?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-002',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'completed',
    startDate: '15.02.2026',
    endDate: '15.02.2026',
    price: 3000,
    views: 10200,
    clicks: 408,
    orders: 12,
    income: 36000,
    adTitle: 'Весенний прогноз 2026 — персональный транзитный отчёт',
    adDescription: 'Специальная акция! Закажите прогноз на весну со скидкой 30%. Узнайте, какие возможности откроются именно для вас.',
    adLink: '/astrologer/elena-star/forecast'
  }, {
    id: 'ORD-A-003',
    type: 'pin',
    name: 'Закреп в каталоге',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '14.03.2026',
    price: 4000,
    views: 8900,
    clicks: 356,
    orders: 9,
    income: 27000,
    adTitle: 'Елена Звёздная — ТОП астролог',
    adDescription: 'Натальные карты, совместимость, прогнозы. Рейтинг 4.9 ★ Более 3000 консультаций.',
    adLink: '/astrologer/elena-star'
  }, {
    id: 'ORD-A-004',
    type: 'banner',
    name: 'Баннер в каталоге',
    status: 'pending_moderation',
    startDate: '10.03.2026',
    endDate: '17.03.2026',
    price: 3500,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    adTitle: 'Кармический анализ — глубокий разбор',
    adDescription: 'Откройте тайны прошлых жизней и поймите свою миссию в этом воплощении. Индивидуальный подход.',
    adLink: '/astrologer/elena-star/karma',
    adImageUrl: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-005',
    type: 'pin',
    name: 'Закреп «Натальная карта»',
    status: 'pending_payment',
    startDate: '12.03.2026',
    endDate: '19.03.2026',
    price: 2000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '12.03.2026',
    adTitle: 'Натальная карта Premium — полный разбор личности',
    adDescription: 'PDF-отчёт на 40+ страниц с детальным анализом всех аспектов вашей карты.',
    adLink: '/astrologer/elena-star/natal'
  }, {
    id: 'ORD-A-006',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'rejected',
    startDate: '08.03.2026',
    endDate: null,
    price: 3000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '08.03.2026',
    rejectionReason: 'Текст рекламы содержит запрещённые обещания. Пожалуйста, уберите гарантии результата и отправьте повторно.',
    adTitle: 'Гарантированное изменение судьбы за 1 консультацию!',
    adDescription: 'Я изменю вашу жизнь за одну сессию. 100% гарантия результата. Запишитесь прямо сейчас!',
    adLink: '/astrologer/elena-star'
  }]);
  const activePlacements = [{
    type: 'Закреп в каталоге',
    category: 'Натальная карта',
    daysLeft: 5,
    views: 1247
  }, {
    type: 'Баннер на главной',
    category: 'Все услуги',
    daysLeft: 3,
    views: 4200
  }];
  // Computed
  const totalAdIncome = adOrders.reduce((sum, o) => sum + o.income, 0);
  const totalAdOrders = adOrders.reduce((sum, o) => sum + o.orders, 0);
  const adOrdersTotalPages = Math.ceil(adOrders.length / AD_ORDERS_PER_PAGE);
  const paginatedAdOrders = adOrders.slice((adOrdersPage - 1) * AD_ORDERS_PER_PAGE, adOrdersPage * AD_ORDERS_PER_PAGE);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'banner':
        return 'Баннер';
      case 'email':
        return 'Рассылка';
      case 'pin':
        return 'Закреп';
      default:
        return type;
    }
  };
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Активен',
          variant: 'success' as const
        };
      case 'completed':
        return {
          label: 'Завершён',
          variant: 'neutral' as const
        };
      case 'pending':
      case 'pending_moderation':
        return {
          label: 'На модерации',
          variant: 'warning' as const
        };
      case 'pending_payment':
        return {
          label: 'Ожидает оплаты',
          variant: 'info' as const
        };
      case 'approved':
        return {
          label: 'Одобрен',
          variant: 'success' as const
        };
      case 'rejected':
        return {
          label: 'Отклонён',
          variant: 'error' as const
        };
      default:
        return {
          label: status,
          variant: 'default' as const
        };
    }
  };
  const handleRepeatOrder = (order: AdOrder) => {
    showToast(`Повторный заказ "${order.name}" создан`);
    console.log(`[Admin Sync] Ad order repeated: ${order.id}`);
  };
  const handleExtendOrder = (order: AdOrder) => {
    showToast(`Продление "${order.name}" оформлено`);
    console.log(`[Admin Sync] Ad order extended: ${order.id}`);
  };
  const handleUpgradeTariff = (tariff: 'pro' | 'top') => {
    setShowTariffModal(false);
    showToast(`Тариф ${tariff === 'pro' ? 'PRO' : 'TOP'} успешно активирован!`);
    console.log(`[Admin Sync] Tariff upgraded to ${tariff}`);
  };
  const handleBuyPin = () => {
    setShowPinModal(false);
    const prices = {
      '1week': 2000,
      '2weeks': 3500,
      '1month': 6000
    };
    showToast(`Закреп "${selectedPinCategory}" оплачен (${prices[selectedPinDuration].toLocaleString('ru-RU')} ₽)`);
    console.log(`[Admin Sync] Pin purchased: ${selectedPinCategory}, duration: ${selectedPinDuration}`);
  };
  return <div className="space-y-6" data-id="element-14362">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2" data-id="element-14363">
        <div data-id="element-14364">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-14365">
            Продвижение профиля
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-14366">
            Увеличивайте заказы и доход
          </p>
        </div>
      </div>

      {/* Ad Budget */}
      <Card padding="md" className="border-slate-200 bg-white" data-id="element-14367">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-14368">
          <div className="flex-1" data-id="element-14369">
            <div className="flex items-center gap-2 mb-2" data-id="element-14370">
              <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center" data-id="element-14371">
                <Wallet className="w-5 h-5 text-purple-600" data-id="element-14372" />
              </div>
              <div data-id="element-14373">
                <h3 className="font-bold text-slate-900 text-sm" data-id="element-14374">
                  Рекламный баланс
                </h3>
                <p className="text-[10px] text-slate-500" data-id="element-14375">
                  Средства для оплаты рекламных размещений
                </p>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mt-3" data-id="element-14376">
              <span className="text-3xl font-bold text-slate-900" data-id="element-14377">
                {adBalance.toLocaleString('ru-RU')} ₽
              </span>
              <Badge variant="success" size="sm" data-id="element-14378">
                доступно
              </Badge>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-500" data-id="element-14379">
              <span data-id="element-14380">
                Потрачено за месяц:{' '}
                <strong className="text-slate-700" data-id="element-14381">3 500 ₽</strong>
              </span>
              <span data-id="element-14382">
                Остаток:{' '}
                <strong className="text-slate-700" data-id="element-14383">
                  {adBalance.toLocaleString('ru-RU')} ₽
                </strong>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto" data-id="element-14384">
            <Button variant="primary" size="md" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 shadow-sm" icon={<Wallet className="w-4 h-4" data-id="element-14386" />} onClick={() => {
            setAdBalance(prev => prev + 5000);
            showToast('Рекламный баланс пополнен на 5 000 ₽ ✓');
          }} data-id="element-14385">
              Пополнить рекламный баланс
            </Button>
            <p className="text-[10px] text-slate-400 text-center sm:text-right" data-id="element-14387">
              Минимум 1 000 ₽
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-500" data-id="element-14388">
          <AlertCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" data-id="element-14389" />
          <span data-id="element-14390">
            Реклама списывается с рекламного баланса. Если средств не хватает —
            пополните баланс перед оформлением заказа.
          </span>
        </div>
      </Card>

      {/* Section 1: Current Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-14391">
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => showToast('Текущий тариф: Базовый')} data-id="element-14392">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14393">Тариф</div>
          <div className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-14394">
            Базовый <Crown className="w-4 h-4 text-amber-400" data-id="element-14395" />
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Позиция #47 в каталоге');
      }} data-id="element-14396">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14397">Позиция</div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14398">#47</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('1 247 просмотров за неделю');
      }} data-id="element-14399">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14400">
            Просмотры
          </div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14401">1 247</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Конверсия 8.3%');
      }} data-id="element-14402">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14403">
            Конверсия
          </div>
          <div className="text-lg font-bold text-emerald-600" data-id="element-14404">8.3%</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-emerald-50/50 border-emerald-200" onClick={() => showToast('Доход с продвижения за месяц')} data-id="element-14405">
          <div className="text-xs text-emerald-600 mb-1 font-medium" data-id="element-14406">
            Доход с рекламы
          </div>
          <div className="text-lg font-bold text-emerald-700" data-id="element-14407">
            {totalAdIncome.toLocaleString('ru-RU')} ₽
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-indigo-50/50 border-indigo-200" onClick={() => showToast('Заказы с продвижения за месяц')} data-id="element-14408">
          <div className="text-xs text-indigo-600 mb-1 font-medium" data-id="element-14409">
            Заказы с рекламы
          </div>
          <div className="text-lg font-bold text-indigo-700" data-id="element-14410">
            {totalAdOrders}
          </div>
        </Card>
      </div>

      {/* Section 2: CTA Block — How to increase income */}
      <Card padding="md" className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white border-none relative overflow-hidden" data-id="element-14411">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl" data-id="element-14412"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-14413">
          <div className="flex-1" data-id="element-14414">
            <div className="flex items-center gap-2 mb-2" data-id="element-14415">
              <Sparkles className="w-5 h-5 text-amber-300" data-id="element-14416" />
              <h3 className="text-xl font-bold" data-id="element-14417">Как увеличить доход</h3>
            </div>
            <p className="text-indigo-100 text-sm max-w-lg" data-id="element-14418">
              Перейдите на тариф <strong className="text-white" data-id="element-14419">PRO</strong> и
              получите приоритет в каталоге. По статистике, PRO-астрологи
              получают{' '}
              <strong className="text-white" data-id="element-14420">на 40% больше заказов</strong>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-14421">
            <button className="px-5 py-2.5 bg-white text-indigo-700 font-bold text-sm rounded-lg shadow-lg hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2" onClick={() => {
            setSelectedTariff('pro');
            setShowTariffModal(true);
          }} data-id="element-14422">
              <Crown className="w-4 h-4" data-id="element-14423" />
              Перейти на PRO
            </button>
            <button className="px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white font-bold text-sm rounded-lg border-2 border-white/50 hover:bg-white/30 transition-colors flex items-center justify-center gap-2" onClick={() => setShowNewAdModal(true)} data-id="element-14424">
              <Megaphone className="w-4 h-4" data-id="element-14425" />
              Включить рекламу
            </button>
          </div>
        </div>
      </Card>

      {/* Section 3: Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-14426">
        {[{
        icon: Rocket,
        label: 'Запустить рекламу',
        color: 'text-indigo-600 bg-indigo-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: ArrowUpCircle,
        label: 'Поднять профиль',
        color: 'text-amber-600 bg-amber-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: Mail,
        label: 'Включить рассылку',
        color: 'text-emerald-600 bg-emerald-50',
        action: () => showToast('Рассылка включена — клиенты получат уведомление о ваших услугах')
      }, {
        icon: Crown,
        label: 'Улучшить тариф',
        color: 'text-purple-600 bg-purple-50',
        action: () => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }
      }].map((item, i) => <button key={i} onClick={item.action} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all text-center group" data-id="element-14427">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color} group-hover:scale-110 transition-transform`} data-id="element-14428">
              <item.icon className="w-5 h-5" data-id="element-14429" />
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-14430">
              {item.label}
            </span>
          </button>)}
      </div>

      {/* Section 4: Ad Orders */}
      <Card padding="none" className="overflow-hidden" data-id="element-14431">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-14432">
          <div data-id="element-14433">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-14434">
              Мои рекламные заказы
            </h3>
            <button onClick={() => setShowHowItWorks(!showHowItWorks)} className="text-xs text-indigo-600 font-medium hover:underline mt-0.5" data-id="element-14435">
              {showHowItWorks ? 'Скрыть справку' : 'Как работает реклама?'}
            </button>
          </div>
          <Button variant="primary" size="sm" icon={<Megaphone className="w-4 h-4" data-id="element-14437" />} onClick={() => setShowNewAdModal(true)} data-id="element-14436">
            Новый заказ
          </Button>
        </div>
        {showHowItWorks && <div className="px-4 sm:px-5 py-4 bg-indigo-50/50 border-b border-indigo-100 animate-in fade-in slide-in-from-top-2" data-id="element-14438">
            <h4 className="font-bold text-indigo-900 text-sm mb-2" data-id="element-14439">
              Как работает реклама на платформе
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-indigo-800" data-id="element-14440">
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14441">
                <div className="font-bold mb-1" data-id="element-14442">1. Оформление и оплата</div>
                <p className="text-indigo-600" data-id="element-14443">
                  Выберите формат, загрузите материалы и оплатите с рекламного
                  баланса.
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14444">
                <div className="font-bold mb-1" data-id="element-14445">2. Модерация (1-12 часов)</div>
                <p className="text-indigo-600" data-id="element-14446">
                  Администратор проверит материалы. Результат придет в системные
                  уведомления (колокольчик).
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14447">
                <div className="font-bold mb-1" data-id="element-14448">3. Запуск и статистика</div>
                <p className="text-indigo-600" data-id="element-14449">
                  После одобрения реклама запустится автоматически. Отслеживайте
                  просмотры и клики здесь.
                </p>
              </div>
            </div>
          </div>}

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-14450">
          <table className="w-full text-left text-sm" data-id="element-14451">
            <thead className="bg-slate-50/50 border-b border-slate-100 text-slate-500" data-id="element-14452">
              <tr data-id="element-14453">
                <th className="px-5 py-3 font-semibold" data-id="element-14454">Услуга</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14455">Тип</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14456">Статус</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14457">Период</th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14458">
                  Стоимость
                </th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14459">
                  Результат
                </th>
                <th className="px-5 py-3 w-10" data-id="element-14460"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-14461">
              {paginatedAdOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const statusConfig = getStatusConfig(order.status);
              return <Fragment key={order.id} data-id="element-14462">
                    <tr className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50/80' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14463">
                      <td className="px-5 py-3" data-id="element-14464">
                        <div className="font-bold text-slate-900" data-id="element-14465">
                          {order.name}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-14466">
                          {order.id}
                        </div>
                      </td>
                      <td className="px-5 py-3" data-id="element-14467">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-14468">
                          {getTypeLabel(order.type)}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-id="element-14469">
                        <Badge variant={statusConfig.variant} size="sm" data-id="element-14470">
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs" data-id="element-14471">
                        {order.endDate ? `${order.startDate} — ${order.endDate}` : `С ${order.startDate}`}
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-14472">
                        {order.price.toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="px-5 py-3 text-right" data-id="element-14473">
                        <span className="text-xs text-slate-500" data-id="element-14474">
                          {order.clicks} кликов / {order.orders} заказов
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-400" data-id="element-14475">
                        {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-14476" /> : <ChevronDown className="w-5 h-5" data-id="element-14477" />}
                      </td>
                    </tr>

                    {isExpanded && <tr className="bg-slate-50/50" data-id="element-14478">
                        <td colSpan={7} className="px-5 py-4" data-id="element-14479">
                          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm animate-in fade-in slide-in-from-top-2" data-id="element-14480">
                            {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-4" data-id="element-14481">
                                <div className="flex items-center gap-2 mb-2" data-id="element-14482">
                                  {order.status === 'rejected' ? <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-14483" /> : order.status === 'pending_payment' ? <Wallet className="w-5 h-5 text-indigo-500" data-id="element-14484" /> : <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-14485" />}
                                  <h4 className="font-bold text-slate-900 text-sm" data-id="element-14486">
                                    {order.status === 'rejected' ? 'Заказ отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Заказ на модерации'}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-600" data-id="element-14487">
                                  {order.status === 'rejected' ? 'Модерация не пройдена. Исправьте замечания и отправьте повторно.' : order.status === 'pending_payment' ? 'Модерация пройдена! Оплатите заказ для запуска рекламы.' : 'Ваша реклама проверяется администратором. Обычно это занимает 1–12 часов.'}
                                </p>

                                {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700" data-id="element-14488">
                                      <div className="font-bold mb-1" data-id="element-14489">
                                        Причина отклонения:
                                      </div>
                                      {order.rejectionReason}
                                    </div>}

                                <div className="relative pt-2 pb-2" data-id="element-14490">
                                  <div className="absolute left-3 top-6 bottom-4 w-0.5 bg-slate-200" data-id="element-14491"></div>

                                  {/* Шаг 1: Оформлен */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-14492">
                                    <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-14493">
                                      <Check className="w-3 h-3" data-id="element-14494" />
                                    </div>
                                    <div data-id="element-14495">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14496">
                                        Оформлен
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-14497">
                                        {order.startDate}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 2: Модерация */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-14498">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-14499">
                                      {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-14500" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-14501" /> : <Clock className="w-3 h-3" data-id="element-14502" />}
                                    </div>
                                    <div data-id="element-14503">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14504">
                                        Модерация
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14505">
                                        {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 3: Оплата */}
                                  <div className={`flex gap-3 mb-3 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-14506">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-14507">
                                      <Wallet className="w-3 h-3" data-id="element-14508" />
                                    </div>
                                    <div data-id="element-14509">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14510">
                                        Оплата
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : order.status === 'approved' ? 'text-emerald-600' : 'text-slate-500'}`} data-id="element-14511">
                                        {order.status === 'pending_payment' ? 'Ожидает оплаты' : order.status === 'approved' ? 'Оплачено' : 'Ожидается'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 4: Запуск */}
                                  <div className={`flex gap-3 relative z-10 ${order.status !== 'approved' ? 'opacity-40' : ''}`} data-id="element-14512">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-14513">
                                      <Rocket className="w-3 h-3" data-id="element-14514" />
                                    </div>
                                    <div data-id="element-14515">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14516">
                                        Запуск
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-14517">
                                        Ожидается
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mt-4 pt-4 border-t border-slate-100" data-id="element-14518">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-14519">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-14520" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-14521">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-14522">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-14523">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14524" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-14525">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-14526">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-14527">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-14528">
                                            <Globe className="w-3 h-3" data-id="element-14529" />
                                            <span className="truncate" data-id="element-14530">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                {/* Action buttons */}
                                <div className="flex flex-wrap gap-2 pt-2" data-id="element-14531">
                                  {order.status === 'pending_payment' && <Button variant="primary" size="sm" icon={<Wallet className="w-3.5 h-3.5" data-id="element-14533" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'active' as const
                            } : o));
                            setAdBalance(prev => prev - order.price);
                            showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                          }} data-id="element-14532">
                                      Оплатить{' '}
                                      {order.price.toLocaleString('ru-RU')} ₽
                                    </Button>}
                                  {order.status === 'rejected' && <Button variant="primary" size="sm" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14535" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'pending_moderation' as const,
                              rejectionReason: undefined
                            } : o));
                            showToast('Заявка отправлена повторно на модерацию');
                          }} data-id="element-14534">
                                      Исправить и отправить
                                    </Button>}
                                </div>
                              </div> : <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-5" data-id="element-14536">
                                  <div data-id="element-14537">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14538">
                                      <Eye className="w-3 h-3" data-id="element-14539" /> Просмотры
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-14540">
                                      {order.views.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-14541">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14542">
                                      <MousePointerClick className="w-3 h-3" data-id="element-14543" />{' '}
                                      Клики
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-14544">
                                      {order.clicks.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-14545">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14546">
                                      <ShoppingBag className="w-3 h-3" data-id="element-14547" /> Заказы
                                    </div>
                                    <div className="text-xl font-bold text-indigo-600" data-id="element-14548">
                                      {order.orders}
                                    </div>
                                  </div>
                                  <div data-id="element-14549">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14550">
                                      <DollarSign className="w-3 h-3" data-id="element-14551" /> Доход
                                    </div>
                                    <div className="text-xl font-bold text-emerald-600" data-id="element-14552">
                                      {order.income.toLocaleString('ru-RU')} ₽
                                    </div>
                                  </div>
                                </div>

                                <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-14553">
                                  CTR:{' '}
                                  <strong className="text-slate-700" data-id="element-14554">
                                    {(order.clicks / order.views * 100).toFixed(1)}
                                    %
                                  </strong>{' '}
                                  · Конверсия:{' '}
                                  <strong className="text-slate-700" data-id="element-14555">
                                    {order.clicks > 0 ? (order.orders / order.clicks * 100).toFixed(1) : 0}
                                    %
                                  </strong>{' '}
                                  · ROI:{' '}
                                  <strong className={order.income > order.price ? 'text-emerald-600' : 'text-rose-600'} data-id="element-14556">
                                    {order.price > 0 ? ((order.income - order.price) / order.price * 100).toFixed(0) : 0}
                                    %
                                  </strong>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mb-4 pt-2" data-id="element-14557">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-14558">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-14559" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-14560">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-14561">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-14562">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14563" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-14564">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-14565">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-14566">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-14567">
                                            <Globe className="w-3 h-3" data-id="element-14568" />
                                            <span className="truncate" data-id="element-14569">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                <div className="flex gap-2" data-id="element-14570">
                                  <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14572" />} onClick={e => {
                            e.stopPropagation();
                            handleRepeatOrder(order);
                          }} data-id="element-14571">
                                    Повторить
                                  </Button>
                                  {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14574" />} onClick={e => {
                            e.stopPropagation();
                            handleExtendOrder(order);
                          }} data-id="element-14573">
                                      Продлить
                                    </Button>}
                                </div>
                              </>}
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-slate-100" data-id="element-14575">
          {paginatedAdOrders.map(order => {
          const isExpanded = expandedOrderId === order.id;
          const statusConfig = getStatusConfig(order.status);
          return <div key={order.id} data-id="element-14576">
                <div className={`p-4 cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14577">
                  <div className="flex justify-between items-start mb-2" data-id="element-14578">
                    <div data-id="element-14579">
                      <div className="font-bold text-slate-900 text-sm" data-id="element-14580">
                        {order.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-14581">
                        {getTypeLabel(order.type)} · {order.id}
                      </div>
                    </div>
                    <Badge variant={statusConfig.variant} size="sm" data-id="element-14582">
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-14583">
                    <span className="text-xs text-slate-500" data-id="element-14584">
                      {order.startDate}
                      {order.endDate ? ` — ${order.endDate}` : ''}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-14585">
                      {order.price.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                </div>

                {isExpanded && <div className="p-4 bg-slate-50 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-14586">
                    {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-3" data-id="element-14587">
                        <div className="flex items-center gap-2" data-id="element-14588">
                          {order.status === 'rejected' ? <AlertCircle className="w-4 h-4 text-rose-500" data-id="element-14589" /> : order.status === 'pending_payment' ? <Wallet className="w-4 h-4 text-indigo-500" data-id="element-14590" /> : <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-14591" />}
                          <h4 className="font-bold text-slate-900 text-sm" data-id="element-14592">
                            {order.status === 'rejected' ? 'Отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'На модерации'}
                          </h4>
                        </div>

                        {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-700" data-id="element-14593">
                              <div className="font-bold mb-0.5" data-id="element-14594">Причина:</div>
                              {order.rejectionReason}
                            </div>}

                        <div className="relative pt-1 pb-1" data-id="element-14595">
                          <div className="absolute left-3 top-5 bottom-3 w-0.5 bg-slate-200" data-id="element-14596"></div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-14597">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-14598">
                              <Check className="w-3 h-3" data-id="element-14599" />
                            </div>
                            <div data-id="element-14600">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14601">
                                Оформлен
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-14602">
                                {order.startDate}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-14603">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-14604">
                              {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-14605" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-14606" /> : <Clock className="w-3 h-3" data-id="element-14607" />}
                            </div>
                            <div data-id="element-14608">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14609">
                                Модерация
                              </div>
                              <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14610">
                                {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                              </div>
                            </div>
                          </div>

                          <div className={`flex gap-3 mb-2.5 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-14611">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : 'bg-slate-100 text-slate-400'}`} data-id="element-14612">
                              <Wallet className="w-3 h-3" data-id="element-14613" />
                            </div>
                            <div data-id="element-14614">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14615">
                                Оплата
                              </div>
                              <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : 'text-slate-500'}`} data-id="element-14616">
                                {order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Ожидается'}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 relative z-10 opacity-40" data-id="element-14617">
                            <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-14618">
                              <Rocket className="w-3 h-3" data-id="element-14619" />
                            </div>
                            <div data-id="element-14620">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14621">
                                Запуск
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-14622">
                                Ожидается
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 border-t border-slate-200" data-id="element-14623">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-14624">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-14625" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-14626">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-14627">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-14628">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14629" />
                                </div>}
                              <div className="p-2.5" data-id="element-14630">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-14631">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-14632">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-14633">
                                    <Globe className="w-2.5 h-2.5" data-id="element-14634" />
                                    <span className="truncate" data-id="element-14635">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex flex-wrap gap-2" data-id="element-14636">
                          {order.status === 'pending_payment' && <Button variant="primary" size="sm" fullWidth icon={<Wallet className="w-3.5 h-3.5" data-id="element-14638" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'active' as const
                    } : o));
                    setAdBalance(prev => prev - order.price);
                    showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                  }} data-id="element-14637">
                              Оплатить {order.price.toLocaleString('ru-RU')} ₽
                            </Button>}
                          {order.status === 'rejected' && <Button variant="primary" size="sm" fullWidth icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14640" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'pending_moderation' as const,
                      rejectionReason: undefined
                    } : o));
                    showToast('Заявка отправлена повторно на модерацию');
                  }} data-id="element-14639">
                              Исправить и отправить
                            </Button>}
                        </div>
                      </div> : <>
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-14641">
                          <div data-id="element-14642">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14643">
                              Просмотры
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-14644">
                              {order.views.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-14645">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14646">
                              Клики
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-14647">
                              {order.clicks.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-14648">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14649">
                              Заказы
                            </div>
                            <div className="font-bold text-indigo-600" data-id="element-14650">
                              {order.orders}
                            </div>
                          </div>
                          <div data-id="element-14651">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14652">
                              Доход
                            </div>
                            <div className="font-bold text-emerald-600" data-id="element-14653">
                              {order.income.toLocaleString('ru-RU')} ₽
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия (активные/завершённые) */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 mb-3 border-t border-slate-200" data-id="element-14654">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-14655">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-14656" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-14657">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-14658">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-14659">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14660" />
                                </div>}
                              <div className="p-2.5" data-id="element-14661">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-14662">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-14663">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-14664">
                                    <Globe className="w-2.5 h-2.5" data-id="element-14665" />
                                    <span className="truncate" data-id="element-14666">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex gap-2" data-id="element-14667">
                          <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14669" />} onClick={e => {
                    e.stopPropagation();
                    handleRepeatOrder(order);
                  }} data-id="element-14668">
                            Повторить
                          </Button>
                          {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14671" />} onClick={e => {
                    e.stopPropagation();
                    handleExtendOrder(order);
                  }} data-id="element-14670">
                              Продлить
                            </Button>}
                        </div>
                      </>}
                  </div>}
              </div>;
        })}
        </div>

        {/* Ad Orders Pagination */}
        {adOrdersTotalPages > 1 && <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-14672">
            <span className="text-xs text-slate-500" data-id="element-14673">
              Показано {(adOrdersPage - 1) * AD_ORDERS_PER_PAGE + 1}–
              {Math.min(adOrdersPage * AD_ORDERS_PER_PAGE, adOrders.length)} из{' '}
              {adOrders.length}
            </span>
            <Pagination currentPage={adOrdersPage} totalPages={adOrdersTotalPages} onPageChange={setAdOrdersPage} data-id="element-14674" />
          </div>}
      </Card>

      {/* Section 5: Tariff Plans */}
      <h3 className="text-lg font-bold text-slate-900 mt-2" data-id="element-14675">Тарифные планы</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-14676">
        {/* Basic */}
        <Card padding="md" className="flex flex-col border-slate-200" data-id="element-14677">
          <div className="mb-4" data-id="element-14678">
            <h4 className="text-lg font-bold text-slate-900 mb-1" data-id="element-14679">Базовый</h4>
            <div className="text-2xl font-bold text-slate-900" data-id="element-14680">Бесплатно</div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-14681">~5-10 заказов/мес</p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14682">
            {['Профиль в каталоге', 'До 3 услуг', 'Стандартная позиция'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14683">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-14684" />{' '}
                  {f}
                </li>)}
          </ul>
          <div className="py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-center text-sm font-medium text-slate-600 flex items-center justify-center gap-2" data-id="element-14685">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-14686" /> Ваш текущий
            план
          </div>
        </Card>

        {/* PRO */}
        <Card padding="md" className="flex flex-col border-indigo-200 shadow-md ring-1 ring-indigo-100 relative" data-id="element-14687">
          <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg rounded-tr-xl uppercase tracking-wider" data-id="element-14688">
            Популярный
          </div>
          <div className="mb-4" data-id="element-14689">
            <h4 className="text-lg font-bold text-indigo-700 mb-1 flex items-center gap-2" data-id="element-14690">
              PRO <ShieldCheck className="w-5 h-5" data-id="element-14691" />
            </h4>
            <div className="flex items-baseline gap-1" data-id="element-14692">
              <span className="text-2xl font-bold text-slate-900" data-id="element-14693">3 000 ₽</span>
              <span className="text-slate-500 text-sm" data-id="element-14694">/ мес</span>
            </div>
            <p className="text-xs text-emerald-600 font-medium mt-1" data-id="element-14695">
              ~20-35 заказов/мес (+40%)
            </p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14696">
            {['Бейдж PRO', 'Приоритет в поиске', 'До 10 услуг', 'Расширенная аналитика', 'Поддержка в чате'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14697">
                <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" data-id="element-14698" /> {f}
              </li>)}
          </ul>
          <Button variant="primary" fullWidth onClick={() => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }} data-id="element-14699">
            Перейти на PRO
          </Button>
        </Card>

        {/* TOP */}
        <Card padding="md" className="flex flex-col bg-gradient-to-b from-amber-50/50 to-white border-amber-200 relative" data-id="element-14700">
          <div className="mb-4" data-id="element-14701">
            <h4 className="text-lg font-bold text-amber-700 mb-1 flex items-center gap-2" data-id="element-14702">
              TOP <Crown className="w-5 h-5" data-id="element-14703" />
            </h4>
            <div className="flex items-baseline gap-1" data-id="element-14704">
              <span className="text-2xl font-bold text-slate-900" data-id="element-14705">7 000 ₽</span>
              <span className="text-slate-500 text-sm" data-id="element-14706">/ мес</span>
            </div>
            <p className="text-xs text-emerald-600 font-medium mt-1" data-id="element-14707">
              ~50-80 заказов/мес (+120%)
            </p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14708">
            {['Всё из PRO', 'Бейдж TOP Астролог', 'Закреп вверху каталога', 'Блок на главной', 'Безлимит услуг', 'Персональный менеджер'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14709">
                <Check className="w-4 h-4 text-amber-500 flex-shrink-0" data-id="element-14710" /> {f}
              </li>)}
          </ul>
          <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" fullWidth onClick={() => {
          setSelectedTariff('top');
          setShowTariffModal(true);
        }} data-id="element-14711">
            Перейти на TOP
          </Button>
        </Card>
      </div>

      {/* Section 6: Active Placements */}
      <Card padding="md" data-id="element-14712">
        <div className="flex justify-between items-center mb-4" data-id="element-14713">
          <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-14714">
            <Target className="w-5 h-5 text-amber-500" data-id="element-14715" /> Где вы размещены
          </h3>
          <Button variant="outline" size="sm" onClick={() => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }} data-id="element-14716">
            Купить размещение
          </Button>
        </div>

        {activePlacements.length > 0 ? <div className="space-y-3" data-id="element-14717">
            {activePlacements.map((p, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-amber-100 bg-amber-50/30" data-id="element-14718">
                <div className="flex items-center gap-3" data-id="element-14719">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-14720">
                    <Target className="w-4 h-4 text-amber-600" data-id="element-14721" />
                  </div>
                  <div data-id="element-14722">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-14723">
                      {p.type}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-14724">{p.category}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm" data-id="element-14725">
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14726">
                    <Clock className="w-3.5 h-3.5 text-amber-500" data-id="element-14727" />
                    <span data-id="element-14728">{p.daysLeft} дн.</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14729">
                    <Eye className="w-3.5 h-3.5 text-indigo-500" data-id="element-14730" />
                    <span data-id="element-14731">{p.views.toLocaleString('ru-RU')}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50 h-8 px-2" onClick={() => {
              setShowPinModal(true);
              setSelectedPinCategory(p.category);
            }} data-id="element-14732">
                    Продлить
                  </Button>
                </div>
              </div>)}
          </div> : <div className="text-center py-8 text-slate-500" data-id="element-14733">
            <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-14734" />
            <p className="text-sm" data-id="element-14735">У вас нет активных размещений</p>
          </div>}
      </Card>

      {/* Section 7: Simple Analytics */}
      <Card padding="md" data-id="element-14736">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-14737">
          <BarChart3 className="w-5 h-5 text-indigo-500" data-id="element-14738" /> Эффективность за
          месяц
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-14739">
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14740">
            <Eye className="w-5 h-5 text-slate-400 mx-auto mb-2" data-id="element-14741" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14742">1 247</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14743">Просмотры</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14744">
            <MousePointerClick className="w-5 h-5 text-indigo-400 mx-auto mb-2" data-id="element-14745" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14746">620</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14747">Клики</div>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-14748">
            <ShoppingBag className="w-5 h-5 text-indigo-500 mx-auto mb-2" data-id="element-14749" />
            <div className="text-2xl font-bold text-indigo-700" data-id="element-14750">39</div>
            <div className="text-xs text-indigo-600 mt-1" data-id="element-14751">Заказы</div>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-14752">
            <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-2" data-id="element-14753" />
            <div className="text-2xl font-bold text-emerald-700" data-id="element-14754">117 000 ₽</div>
            <div className="text-xs text-emerald-600 mt-1" data-id="element-14755">Доход</div>
          </div>
        </div>
      </Card>

      {/* Tariff Upgrade Modal */}
      {showTariffModal && selectedTariff && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14756">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-14757">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-14758">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14759">
                Переход на {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
              </h3>
              <button onClick={() => setShowTariffModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14760">
                <X className="w-5 h-5" data-id="element-14761" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-14762">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14763">
                <div className="flex justify-between text-sm mb-3" data-id="element-14764">
                  <span className="text-slate-500" data-id="element-14765">Тариф</span>
                  <span className="font-bold text-slate-900" data-id="element-14766">
                    {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14767">
                  <span className="text-slate-500" data-id="element-14768">Стоимость</span>
                  <span className="font-bold text-slate-900" data-id="element-14769">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽ / мес
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14770">
                  <span className="text-slate-500" data-id="element-14771">Ожидаемый рост заказов</span>
                  <span className="font-bold text-emerald-600" data-id="element-14772">
                    {selectedTariff === 'pro' ? '+40%' : '+120%'}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-slate-200" data-id="element-14773">
                  <span className="font-bold text-slate-900" data-id="element-14774">
                    К оплате сейчас
                  </span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-14775">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-500" data-id="element-14776">
                Оплата будет списана с вашего баланса. Тариф активируется
                мгновенно. Отмена возможна в любое время.
              </p>

              <div className="flex gap-3" data-id="element-14777">
                <Button variant="outline" fullWidth onClick={() => setShowTariffModal(false)} data-id="element-14778">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={() => handleUpgradeTariff(selectedTariff)} data-id="element-14779">
                  Оплатить и активировать
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* New Ad Order Modal */}
      {showNewAdModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14780">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto" data-id="element-14781">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 sticky top-0 bg-white z-10" data-id="element-14782">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14783">
                Создать рекламу
              </h3>
              <button onClick={() => {
            setShowNewAdModal(false);
            setSelectedAdSlot(null);
            setAdWizardStep(1);
            setAdMaterials({
              title: '',
              description: '',
              link: '',
              imageUrl: ''
            });
          }} className="text-slate-400 hover:text-slate-600" data-id="element-14784">
                <X className="w-5 h-5" data-id="element-14785" />
              </button>
            </div>
            <div className="p-5 space-y-4" data-id="element-14786">
              {/* Progress Steps */}
              <div className="flex items-center justify-between mb-6 relative" data-id="element-14787">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-100 -z-10" data-id="element-14788"></div>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-indigo-500 -z-10 transition-all duration-300" style={{
              width: `${(adWizardStep - 1) * 50}%`
            }} data-id="element-14789"></div>

                {[1, 2, 3].map(step => <div key={step} className="flex flex-col items-center gap-1 bg-white px-2" data-id="element-14790">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${adWizardStep >= step ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-400'}`} data-id="element-14791">
                      {adWizardStep > step ? <Check className="w-4 h-4" data-id="element-14792" /> : step}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${adWizardStep >= step ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-14793">
                      {step === 1 ? 'Формат' : step === 2 ? 'Материалы' : 'Оплата'}
                    </span>
                  </div>)}
              </div>

              {adWizardStep === 1 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14794">
                  <p className="text-sm text-slate-600" data-id="element-14795">
                    Выберите рекламный слот. Каждый формат показывается в разных
                    местах сайта.
                  </p>

                  <div className="max-h-[45vh] overflow-y-auto space-y-3 pr-1 -mr-1" data-id="element-14796">
                    {availableAdSlots.map(slot => <button key={slot.id} onClick={() => setSelectedAdSlot(selectedAdSlot === slot.id ? null : slot.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedAdSlot === slot.id ? slot.activeCls : 'border-slate-200 hover:border-slate-300'}`} data-id="element-14797">
                        <div className="flex items-start gap-3" data-id="element-14798">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${slot.iconCls}`} data-id="element-14799">
                            <slot.icon className="w-5 h-5" data-id="element-14800" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-14801">
                            <div className="flex items-center justify-between gap-2" data-id="element-14802">
                              <span className="font-bold text-slate-900 text-sm" data-id="element-14803">
                                {slot.name}
                              </span>
                              <span className="text-xs font-bold text-indigo-600 whitespace-nowrap" data-id="element-14804">
                                {slot.price}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-14805">
                              {slot.where}
                            </p>
                            {selectedAdSlot === slot.id && <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 animate-in fade-in" data-id="element-14806">
                                <div className="flex items-center gap-2 text-xs" data-id="element-14807">
                                  <Eye className="w-3.5 h-3.5 text-slate-400" data-id="element-14808" />
                                  <span className="text-slate-600" data-id="element-14809">
                                    Охват:{' '}
                                    <strong className="text-slate-900" data-id="element-14810">
                                      {slot.reach}
                                    </strong>
                                  </span>
                                </div>
                                <div className="flex items-start gap-2 text-xs" data-id="element-14811">
                                  <AlertCircle className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-14812" />
                                  <span className="text-slate-600" data-id="element-14813">
                                    Требования:{' '}
                                    <strong className="text-slate-900" data-id="element-14814">
                                      {slot.requirements}
                                    </strong>
                                  </span>
                                </div>
                              </div>}
                          </div>
                        </div>
                      </button>)}
                  </div>

                  <div className="flex justify-end pt-2" data-id="element-14815">
                    <Button variant="primary" disabled={!selectedAdSlot} onClick={() => {
                const selected = availableAdSlots.find(s => s.id === selectedAdSlot);
                if (selected?.slotType === 'Pinned') {
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setShowPinModal(true);
                  setSelectedPinCategory('Натальная карта');
                } else {
                  setAdWizardStep(2);
                }
              }} icon={<ArrowRight className="w-4 h-4" data-id="element-14817" />} data-id="element-14816">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 2 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14818">
                  <div data-id="element-14819">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14820">
                      Заголовок рекламы
                    </label>
                    <input type="text" value={adMaterials.title} onChange={e => setAdMaterials({
                ...adMaterials,
                title: e.target.value
              })} placeholder="Например: Профессиональный разбор натальной карты" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14821" />
                    <div className="text-right text-xs text-slate-400 mt-1" data-id="element-14822">
                      {adMaterials.title.length}/50
                    </div>
                  </div>

                  <div data-id="element-14823">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14824">
                      Описание
                    </label>
                    <textarea value={adMaterials.description} onChange={e => setAdMaterials({
                ...adMaterials,
                description: e.target.value
              })} placeholder="Кратко опишите ваше предложение..." rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" data-id="element-14825" />
                  </div>

                  <div data-id="element-14826">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14827">
                      Ссылка (куда вести)
                    </label>
                    <input type="text" value={adMaterials.link} onChange={e => setAdMaterials({
                ...adMaterials,
                link: e.target.value
              })} placeholder="https://astroguide.ru/profile/elena" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14828" />
                  </div>

                  <div data-id="element-14829">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14830">
                      Баннер / Изображение
                    </label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:bg-slate-50 hover:border-indigo-300 cursor-pointer transition-colors relative overflow-hidden group" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = e => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const url = URL.createObjectURL(file);
                    setAdMaterials({
                      ...adMaterials,
                      imageUrl: url
                    });
                    showToast(`Изображение загружено ✓`);
                  }
                };
                input.click();
              }} data-id="element-14831">
                      {adMaterials.imageUrl ? <>
                          <img src={adMaterials.imageUrl} alt="Banner" className="absolute inset-0 w-full h-full object-cover" data-id="element-14832" />
                          <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-14833">
                            <span className="text-white font-medium text-sm" data-id="element-14834">
                              Заменить изображение
                            </span>
                          </div>
                        </> : <>
                          <LayoutGrid className="w-8 h-8 text-slate-400 mx-auto mb-2" data-id="element-14835" />
                          <div className="text-sm font-bold text-slate-700 mb-1" data-id="element-14836">
                            Загрузить изображение
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-14837">
                            JPG, PNG до 5 МБ
                          </div>
                        </>}
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-14838">
                    <Button variant="outline" onClick={() => setAdWizardStep(1)} data-id="element-14839">
                      Назад
                    </Button>
                    <Button variant="primary" disabled={!adMaterials.title || !adMaterials.imageUrl} onClick={() => setAdWizardStep(3)} icon={<ArrowRight className="w-4 h-4" data-id="element-14841" />} data-id="element-14840">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 3 && (() => {
            const selectedSlot = availableAdSlots.find(s => s.id === selectedAdSlot);
            const price = selectedSlot?.priceNum || 0;
            const slotName = selectedSlot?.name || 'Реклама';
            const slotType = selectedSlot?.slotType === 'Email' ? 'email' : selectedSlot?.slotType === 'Pinned' ? 'pin' : 'banner';
            return <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14842">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14843">
                        <h4 className="font-bold text-slate-900 mb-3" data-id="element-14844">
                          Сводка заказа
                        </h4>

                        <div className="space-y-2 mb-4" data-id="element-14845">
                          <div className="flex justify-between text-sm" data-id="element-14846">
                            <span className="text-slate-500" data-id="element-14847">Формат</span>
                            <span className="font-medium text-slate-900" data-id="element-14848">
                              {slotName}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-14849">
                            <span className="text-slate-500" data-id="element-14850">Период</span>
                            <span className="font-medium text-slate-900" data-id="element-14851">
                              1 неделя
                            </span>
                          </div>
                        </div>

                        <div className="p-3 bg-white rounded-lg border border-slate-200 mb-4" data-id="element-14852">
                          <div className="text-xs text-slate-500 mb-2" data-id="element-14853">
                            Превью:
                          </div>
                          <div className="flex gap-3 items-center" data-id="element-14854">
                            <div className="w-16 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-14855">
                              {adMaterials.imageUrl && <img src={adMaterials.imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-14856" />}
                            </div>
                            <div className="min-w-0" data-id="element-14857">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-14858">
                                {adMaterials.title}
                              </div>
                              <div className="text-xs text-slate-500 truncate" data-id="element-14859">
                                {adMaterials.description || 'Без описания'}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-14860">
                          <span className="font-bold text-slate-900" data-id="element-14861">
                            К оплате
                          </span>
                          <span className="text-xl font-bold text-indigo-600" data-id="element-14862">
                            {price.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-14863">
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-2" data-id="element-14864">
                          <span className="text-sm font-bold text-indigo-900 flex items-center gap-2" data-id="element-14865">
                            <Wallet className="w-4 h-4" data-id="element-14866" /> Рекламный баланс
                          </span>
                          <span className="font-bold text-indigo-700" data-id="element-14867">
                            {adBalance.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>

                        {adBalance < price ? <div className="text-xs text-rose-600 mt-2 flex items-start gap-1" data-id="element-14868">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-14869" />
                            <span data-id="element-14870">
                              Недостаточно средств. Пополните баланс на{' '}
                              {(price - adBalance).toLocaleString('ru-RU')} ₽
                            </span>
                          </div> : <div className="text-xs text-indigo-600 mt-2" data-id="element-14871">
                            Остаток после оплаты:{' '}
                            {(adBalance - price).toLocaleString('ru-RU')} ₽
                          </div>}
                      </div>

                      <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-14872">
                        <Button variant="outline" onClick={() => setAdWizardStep(2)} data-id="element-14873">
                          Назад
                        </Button>
                        <Button variant="primary" disabled={adBalance < price} onClick={() => {
                  const newOrder: AdOrder = {
                    id: `ORD-A-00${adOrders.length + 1}`,
                    type: slotType as any,
                    name: slotName,
                    status: 'pending',
                    startDate: new Date().toLocaleDateString('ru-RU'),
                    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('ru-RU'),
                    price: price,
                    views: 0,
                    clicks: 0,
                    orders: 0,
                    income: 0
                  };
                  setAdOrders([newOrder, ...adOrders]);
                  setAdBalance(adBalance - price);
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setAdWizardStep(1);
                  setAdMaterials({
                    title: '',
                    description: '',
                    link: '',
                    imageUrl: ''
                  });
                  showToast('Заказ оплачен и отправлен на модерацию. Вы получите уведомление о статусе.');
                }} data-id="element-14874">
                          Оплатить и отправить
                        </Button>
                      </div>
                    </div>;
          })()}
            </div>
          </div>
        </div>}

      {/* Pin Purchase Modal */}
      {showPinModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14875">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-14876">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-14877">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14878">
                Купить размещение
              </h3>
              <button onClick={() => setShowPinModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14879">
                <X className="w-5 h-5" data-id="element-14880" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-14881">
              <div data-id="element-14882">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14883">
                  Категория
                </label>
                <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={selectedPinCategory} onChange={e => setSelectedPinCategory(e.target.value)} data-id="element-14884">
                  <option value="Натальная карта" data-id="element-14885">Натальная карта</option>
                  <option value="Совместимость" data-id="element-14886">Совместимость</option>
                  <option value="Соляр" data-id="element-14887">Соляр на год</option>
                  <option value="Транзиты" data-id="element-14888">Транзиты</option>
                </select>
              </div>

              <div data-id="element-14889">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14890">
                  Длительность
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-14891">
                  {[{
                value: '1week' as const,
                label: '1 неделя',
                price: '2 000 ₽'
              }, {
                value: '2weeks' as const,
                label: '2 недели',
                price: '3 500 ₽'
              }, {
                value: '1month' as const,
                label: '1 месяц',
                price: '6 000 ₽'
              }].map(opt => <button key={opt.value} onClick={() => setSelectedPinDuration(opt.value)} className={`p-3 rounded-xl border text-center transition-all ${selectedPinDuration === opt.value ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-14892">
                      <div className="text-sm font-bold text-slate-900" data-id="element-14893">
                        {opt.label}
                      </div>
                      <div className="text-xs text-indigo-600 font-medium mt-1" data-id="element-14894">
                        {opt.price}
                      </div>
                    </button>)}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14895">
                <div className="flex justify-between text-sm mb-2" data-id="element-14896">
                  <span className="text-slate-500" data-id="element-14897">Категория</span>
                  <span className="font-medium text-slate-900" data-id="element-14898">
                    {selectedPinCategory}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-slate-200" data-id="element-14899">
                  <span className="font-bold text-slate-900" data-id="element-14900">К оплате</span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-14901">
                    {selectedPinDuration === '1week' ? '2 000' : selectedPinDuration === '2weeks' ? '3 500' : '6 000'}{' '}
                    ₽
                  </span>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-14902">
                <Button variant="outline" fullWidth onClick={() => setShowPinModal(false)} data-id="element-14903">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={handleBuyPin} data-id="element-14904">
                  Оплатить
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}